import type { SignalMessage } from '../signalling.ts';

export interface ClockSample {
  clientSendMs: number;
  serverReceiveMs: number;
  serverSendMs: number;
  clientReceiveMs: number;
}

export interface ClockEstimate {
  offsetMs: number;
  roundTripMs: number;
  sample: ClockSample;
}

export const epochNow = (): number => performance.timeOrigin + performance.now();

export function estimateClockSample(sample: ClockSample): ClockEstimate {
  const roundTripMs = (sample.clientReceiveMs - sample.clientSendMs) - (sample.serverSendMs - sample.serverReceiveMs);
  const offsetMs = ((sample.serverReceiveMs - sample.clientSendMs) + (sample.serverSendMs - sample.clientReceiveMs)) / 2;
  return { offsetMs, roundTripMs, sample };
}

export function minimumRttEstimate(samples: readonly ClockSample[]): ClockEstimate | undefined {
  return samples
    .map(estimateClockSample)
    .filter((estimate) => Number.isFinite(estimate.offsetMs) && Number.isFinite(estimate.roundTripMs) && estimate.roundTripMs >= 0)
    .sort((a, b) => a.roundTripMs - b.roundTripMs)[0];
}

export class ClockSynchronizer {
  readonly #socket: WebSocket;
  readonly #samples: ClockSample[] = [];
  readonly #sent = new Set<number>();
  #started = false;
  #timer = 0;
  offsetMs = 0;
  roundTripMs = Number.POSITIVE_INFINITY;
  complete = false;

  constructor(socket: WebSocket) {
    this.#socket = socket;
    if (socket.readyState === WebSocket.OPEN) this.start();
    else socket.addEventListener('open', () => this.start(), { once: true });
    socket.addEventListener('close', () => clearTimeout(this.#timer));
  }

  start(): void {
    if (this.#started) return;
    this.#started = true;
    this.#ping();
  }

  #ping(): void {
    if (this.#socket.readyState !== WebSocket.OPEN || this.#samples.length >= 8) return;
    const clientSend = epochNow();
    this.#sent.add(clientSend);
    this.#socket.send(JSON.stringify({ type: 'ping', clientSend }));
  }

  handle(message: SignalMessage): void {
    if (message.type !== 'pong' || !this.#sent.delete(message.clientSend)) return;
    this.#samples.push({
      clientSendMs: message.clientSend,
      serverReceiveMs: message.serverReceive,
      serverSendMs: message.serverSend,
      clientReceiveMs: epochNow(),
    });
    const best = minimumRttEstimate(this.#samples);
    if (best) {
      this.offsetMs = best.offsetMs;
      this.roundTripMs = best.roundTripMs;
    }
    this.complete = this.#samples.length >= 8;
    if (!this.complete) this.#timer = window.setTimeout(() => this.#ping(), 45);
  }
}
